import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { toast } from 'components/ui/use-toast';
import { useSaveOrgTemplate } from '../hooks';

const MIN_TITLE = 3;

export interface SaveAsTemplateTarget {
  testId: string;
  title: string;
}

interface SaveAsTemplateDialogProps {
  target: SaveAsTemplateTarget | null;
  onClose: () => void;
}

/** Saves one of the org's tests as a template only this organization sees. */
export function SaveAsTemplateDialog({ target, onClose }: SaveAsTemplateDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveOrgTemplate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (target) {
      setTitle(target.title);
      setDescription('');
    }
  }, [target]);

  const canSubmit = title.trim().length >= MIN_TITLE && !save.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target || !canSubmit) return;
    try {
      await save.mutateAsync({ testId: target.testId, title: title.trim(), description: description.trim() || undefined });
      toast.success(t('templates.saved'), { description: t('templates.saved_desc') });
      onClose();
      navigate('/app/tests/templates?source=org');
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(t('templates.save_failed'), { description: message });
    }
  };

  return (
    <Dialog open={Boolean(target)} onOpenChange={(open) => !open && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('templates.save_title')}</DialogTitle>
            <DialogDescription>{t('templates.save_desc')}</DialogDescription>
          </DialogHeader>
          <div className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-template-title">{t('templates.template_name')}</Label>
              <Input id="org-template-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} autoFocus required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-template-desc">
                {t('templates.template_description')} <span className="font-normal text-muted-foreground">· {t('auth.ob_optional')}</span>
              </Label>
              <Textarea id="org-template-desc" rows={3} maxLength={600} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="mt-6 gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
              {t('templates.save_action')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
